import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { FaTimes, FaStar, FaRegStar, FaPaperPlane, FaChevronRight, FaChevronLeft, FaCheckCircle } from 'react-icons/fa';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';
import { obterIdTenent } from '../../services/seguranca';
import { IdEmpresaAdm } from '../../services/utilitarios';

interface PerguntaDto {
  id: string;
  texto: string;
  tipoPergunta: number;
  opcoes: string;
  ordem: number;
  obrigatoria: boolean;
}

interface PesquisaDto {
  id: string;
  titulo: string;
  descricao: string;
  tipo: number;
  perguntas: PerguntaDto[];
}

// ======== ANIMATIONS ========
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const scaleIn = keyframes`from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); }`;
const checkPop = keyframes`from { transform: scale(0); } to { transform: scale(1); }`;

// ======== OVERLAY ========
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1055;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  animation: ${scaleIn} 0.2s ease-out;
  overflow: hidden;
  position: relative;
`;

// ======== HEADER ========
const Header = styled.div`
  padding: 24px 24px 16px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.15s;

  &:hover { background: #e2e8f0; color: #334155; }
`;

const HeaderTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
  padding-right: 40px;
`;

const HeaderDesc = styled.p`
  font-size: 0.82rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

const ProgressTrack = styled.div`
  height: 2px;
  background: #f1f5f9;
`;

const ProgressFill = styled.div<{ pct: number }>`
  height: 100%;
  background: #3b82f6;
  width: ${p => p.pct}%;
  transition: width 0.35s ease;
`;

// ======== BODY ========
const Body = styled.div`
  padding: 20px 24px 24px;
  min-height: 160px;
`;

const PerguntaTexto = styled.p`
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 20px;
  line-height: 1.45;
`;

const Required = styled.span`color: #ef4444; font-weight: 400;`;

const StepLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
`;

const TextInput = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  color: #1e293b;
  transition: border-color 0.15s;

  &:focus { border-color: #3b82f6; outline: none; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); }
  &::placeholder { color: #94a3b8; }
`;

const StarsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 16px 0 8px;
`;

const StarBtn = styled.button<{ active: boolean }>`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 2.2rem;
  color: ${p => p.active ? '#f59e0b' : '#e2e8f0'};
  transition: transform 0.12s, color 0.12s;
  padding: 2px;
  line-height: 1;

  &:hover { transform: scale(1.15); color: #f59e0b; }
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  color: #94a3b8;
  padding: 0 8px;
`;

const NpsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(11, 1fr);
  gap: 5px;
`;

const NpsBtn = styled.button<{ active: boolean; score: number }>`
  height: 40px;
  border-radius: 8px;
  border: 1.5px solid ${p => p.active ? (p.score <= 6 ? '#ef4444' : p.score <= 8 ? '#f59e0b' : '#22c55e') : '#e5e7eb'};
  background: ${p => p.active ? (p.score <= 6 ? '#fef2f2' : p.score <= 8 ? '#fffbeb' : '#f0fdf4') : '#fafafa'};
  color: ${p => p.active ? (p.score <= 6 ? '#dc2626' : p.score <= 8 ? '#d97706' : '#16a34a') : '#6b7280'};
  font-weight: 700;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.1s;

  &:hover { border-color: #93c5fd; background: #eff6ff; color: #3b82f6; }
`;

const OptionBtn = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  margin-bottom: 6px;
  border: 1.5px solid ${p => p.active ? '#3b82f6' : '#e5e7eb'};
  border-radius: 10px;
  background: ${p => p.active ? '#eff6ff' : '#fff'};
  color: ${p => p.active ? '#1d4ed8' : '#374151'};
  font-size: 0.88rem;
  font-weight: ${p => p.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.1s;
  gap: 10px;

  &:hover { border-color: #93c5fd; background: #f8fafc; }
  &:last-child { margin-bottom: 0; }
`;

const Radio = styled.span<{ active: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${p => p.active ? '#3b82f6' : '#d1d5db'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.1s;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.active ? '#3b82f6' : 'transparent'};
  }
`;

// ======== FOOTER ========
const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StepCounter = styled.span`
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
`;

const NavBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const Btn = styled.button<{ primary?: boolean }>`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  display: flex;
  align-items: center;
  gap: 6px;
  border: ${p => p.primary ? 'none' : '1.5px solid #e2e8f0'};
  background: ${p => p.primary ? '#1e40af' : '#fff'};
  color: ${p => p.primary ? '#fff' : '#64748b'};

  &:hover { background: ${p => p.primary ? '#1e3a8a' : '#f8fafc'}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ======== SUCCESS STATE ========
const SuccessView = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f0fdf4;
  color: #22c55e;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 1.5rem;
  animation: ${checkPop} 0.3s ease-out;
`;

const SuccessTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px;
`;

const SuccessDesc = styled.p`
  font-size: 0.85rem;
  color: #64748b;
  margin: 0 0 20px;
`;

const SuccessBtn = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #e2e8f0; }
`;

// ======== COMPONENT ========
const PesquisaBanner: React.FC = () => {
  const token = useAppSelector(state => state.token);
  const logado = useAppSelector(state => state.logado);
  const tenantId = obterIdTenent(token);

  const [pesquisas, setPesquisas] = useState<PesquisaDto[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [pesquisaAtual, setPesquisaAtual] = useState<PesquisaDto | null>(null);
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    if (!logado || !token || tenantId === IdEmpresaAdm) return;

    let timeoutId: NodeJS.Timeout;
    const carregar = async () => {
      try {
        const res = await api.get('/PesquisasPendentes/');
        const data: PesquisaDto[] = res.data || [];
        if (data.length === 0) return;

        // Verifica se já dispensou nesta sessão
        const dismissed = sessionStorage.getItem('pesquisas_dismissed');
        if (dismissed === 'true') return;

        setPesquisas(data);

        // Abre automaticamente depois de 3s (após changelog e treinamento)
        timeoutId = setTimeout(() => {
          setPesquisaAtual(data[0]);
          setShowModal(true);
        }, 3000);
      } catch { /* silently fail */ }
    };
    carregar();

    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [logado, token, tenantId]);

  const totalSteps = pesquisaAtual?.perguntas?.length || 0;
  const perguntaAtual = pesquisaAtual?.perguntas?.[step];
  const isLast = step === totalSteps - 1;

  const fechar = useCallback(() => {
    setShowModal(false);
    setPesquisaAtual(null);
    setSucesso(false);
    sessionStorage.setItem('pesquisas_dismissed', 'true');
  }, []);

  const setResposta = (idPergunta: string, valor: string, autoNext = false) => {
    setRespostas(prev => ({ ...prev, [idPergunta]: valor }));
    if (autoNext && step < totalSteps - 1) {
      setTimeout(() => setStep(s => s + 1), 350);
    }
  };

  const enviar = async () => {
    if (!pesquisaAtual) return;

    const obrigatorias = pesquisaAtual.perguntas.filter(p => p.obrigatoria);
    const faltando = obrigatorias.find(p => !respostas[p.id]?.trim());
    if (faltando) {
      toast.warning('Responda todas as perguntas obrigatorias');
      setStep(pesquisaAtual.perguntas.findIndex(p => p.id === faltando.id));
      return;
    }

    setEnviando(true);
    try {
      await api.post('/PesquisaResponder/', {
        idPesquisa: pesquisaAtual.id,
        itens: Object.entries(respostas).map(([idPesquisaPergunta, valor]) => ({
          idPesquisaPergunta,
          valor,
        })),
      });

      const restantes = pesquisas.filter(p => p.id !== pesquisaAtual.id);
      setPesquisas(restantes);
      setSucesso(true);

      // Se tem mais pesquisas, abre a próxima após 2s
      if (restantes.length > 0) {
        setTimeout(() => {
          setSucesso(false);
          setPesquisaAtual(restantes[0]);
          setRespostas({});
          setStep(0);
        }, 2000);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.Mensagem || 'Erro ao enviar resposta');
    } finally {
      setEnviando(false);
    }
  };

  const renderInput = (p: PerguntaDto) => {
    const valor = respostas[p.id] || '';

    switch (p.tipoPergunta) {
      case 0:
        return <TextInput value={valor} onChange={e => setResposta(p.id, e.target.value)} placeholder="Escreva aqui..." />;

      case 1:
        return (
          <>
            <StarsRow>
              {[1, 2, 3, 4, 5].map(s => (
                <StarBtn key={s} active={Number(valor) >= s} onClick={() => setResposta(p.id, String(s), true)}>
                  {Number(valor) >= s ? <FaStar /> : <FaRegStar />}
                </StarBtn>
              ))}
            </StarsRow>
            <ScaleLabels><span>Ruim</span><span>Excelente</span></ScaleLabels>
          </>
        );

      case 2: {
        const opcoes = p.opcoes ? p.opcoes.split(',').map(o => o.trim()).filter(Boolean) : [];
        return (
          <div>
            {opcoes.map(opt => (
              <OptionBtn key={opt} active={valor === opt} onClick={() => setResposta(p.id, opt, true)}>
                <Radio active={valor === opt} /> {opt}
              </OptionBtn>
            ))}
          </div>
        );
      }

      case 3:
        return (
          <div style={{ display: 'flex', gap: 10 }}>
            <OptionBtn active={valor === 'Sim'} onClick={() => setResposta(p.id, 'Sim', true)} style={{ flex: 1, justifyContent: 'center' }}>
              <Radio active={valor === 'Sim'} /> Sim
            </OptionBtn>
            <OptionBtn active={valor === 'Nao'} onClick={() => setResposta(p.id, 'Nao', true)} style={{ flex: 1, justifyContent: 'center' }}>
              <Radio active={valor === 'Nao'} /> Nao
            </OptionBtn>
          </div>
        );

      case 4:
        return (
          <>
            <NpsGrid>
              {Array.from({ length: 11 }, (_, i) => i).map(n => (
                <NpsBtn key={n} active={valor === String(n)} score={n} onClick={() => setResposta(p.id, String(n), true)}>
                  {n}
                </NpsBtn>
              ))}
            </NpsGrid>
            <ScaleLabels><span>Nada provavel</span><span>Muito provavel</span></ScaleLabels>
          </>
        );

      default:
        return null;
    }
  };

  if (!showModal || !pesquisaAtual) return null;

  // Tela de sucesso
  if (sucesso) {
    return (
      <Overlay id="pesquisa-overlay">
        <Modal>
          <SuccessView>
            <SuccessIcon><FaCheckCircle /></SuccessIcon>
            <SuccessTitle>Obrigado!</SuccessTitle>
            <SuccessDesc>Sua resposta foi registrada com sucesso.</SuccessDesc>
            {pesquisas.length === 0 && (
              <SuccessBtn onClick={fechar}>Fechar</SuccessBtn>
            )}
          </SuccessView>
        </Modal>
      </Overlay>
    );
  }

  if (!perguntaAtual) return null;

  return (
    <Overlay id="pesquisa-overlay" onClick={fechar}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <CloseBtn onClick={fechar}><FaTimes /></CloseBtn>
          <HeaderTitle>{pesquisaAtual.titulo}</HeaderTitle>
          {pesquisaAtual.descricao && <HeaderDesc>{pesquisaAtual.descricao}</HeaderDesc>}
        </Header>

        <ProgressTrack>
          <ProgressFill pct={((step + 1) / totalSteps) * 100} />
        </ProgressTrack>

        <Body>
          <StepLabel>Pergunta {step + 1} de {totalSteps}</StepLabel>
          <PerguntaTexto>
            {perguntaAtual.texto}
            {perguntaAtual.obrigatoria && <Required> *</Required>}
          </PerguntaTexto>
          {renderInput(perguntaAtual)}
        </Body>

        <Footer>
          <StepCounter>
            {Math.round(((step + 1) / totalSteps) * 100)}% completo
          </StepCounter>
          <NavBtns>
            {step > 0 && (
              <Btn onClick={() => setStep(step - 1)}><FaChevronLeft /> Voltar</Btn>
            )}
            {!isLast ? (
              <Btn primary onClick={() => setStep(step + 1)}>
                Continuar <FaChevronRight />
              </Btn>
            ) : (
              <Btn primary onClick={enviar} disabled={enviando}>
                <FaPaperPlane /> {enviando ? 'Enviando...' : 'Enviar'}
              </Btn>
            )}
          </NavBtns>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default PesquisaBanner;
